/**
 * JobForm Component
 * Form for creating and editing jobs with modern Tailwind CSS design
 */

import React, { useState, useEffect } from 'react';
import { Job, JobCondition, CreateJobPayload } from '../../types/job';
import { JOB_TEMPLATES, getTemplate } from '../../config/jobTemplates';
import { FileSelector } from './FileSelector';
import { AlertCircle } from 'lucide-react';

interface JobFormProps {
  job?: Job;
  onSubmit: (payload: CreateJobPayload | Omit<Job, 'id' | 'status' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  onFileUpload: (file: File) => Promise<string>;
  loading?: boolean;
}

export const JobForm: React.FC<JobFormProps> = ({ job, onSubmit, onCancel, onFileUpload, loading = false }) => {
  const [formData, setFormData] = useState({
    name: job?.name || '',
    templateId: job?.templateId || JOB_TEMPLATES[0]?.id || '',
    description: job?.description || '',
    condition: job?.condition || JobCondition.ONCE,
    scheduledFor: job?.scheduledFor || '',
    customCron: job?.customCron || '',
    parameters: job?.parameters || {},
  });

  const [selectedFiles, setSelectedFiles] = useState<any[]>(job?.inputFile ? [job.inputFile] : []);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedTemplate = getTemplate(formData.templateId);

  useEffect(() => {
    if (selectedTemplate) {
      setFormData((prev) => ({
        ...prev,
        parameters: selectedTemplate.parameters,
      }));
    }
  }, [selectedTemplate?.id]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Job name is required';
    }
    if (!formData.templateId) {
      newErrors.templateId = 'Template is required';
    }
    if (formData.condition === JobCondition.CUSTOM && !formData.customCron.trim()) {
      newErrors.customCron = 'Cron expression is required for custom condition';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // If one or more files selected, create a parent job and child jobs
    if (selectedFiles.length >= 1) {
      // Prepare all payloads first
      const parentPayload = {
        name: formData.name,
        templateId: formData.templateId,
        category: selectedTemplate?.category || null,
        description: formData.description,
        condition: formData.condition,
        inputFileId: undefined,
        parameters: formData.parameters,
        scheduledFor: formData.scheduledFor || undefined,
        customCron: formData.condition === JobCondition.CUSTOM ? formData.customCron : undefined,
        isParentJob: true,
      };

      const childPayloads = selectedFiles.map((file) => ({
        name: file.name,
        templateId: formData.templateId,
        category: selectedTemplate?.category || null,
        description: formData.description,
        condition: formData.condition,
        inputFileId: file.id,
        parameters: formData.parameters,
        scheduledFor: formData.scheduledFor || undefined,
        customCron: formData.condition === JobCondition.CUSTOM ? formData.customCron : undefined,
        isChildJob: true,
        parentJobName: formData.name,
      }));

      // Submit parent job with all child jobs
      // Pass them as a special property for batch processing
      const batchPayload = {
        ...parentPayload,
        childJobs: childPayloads,
        isBatchJob: true,
      };

      onSubmit(batchPayload as any);
    } else {
      // No files selected
      const payload = {
        name: formData.name,
        templateId: formData.templateId,
        category: selectedTemplate?.category || null,
        description: formData.description,
        condition: formData.condition,
        inputFileId: undefined,
        parameters: formData.parameters,
        scheduledFor: formData.scheduledFor || undefined,
        customCron: formData.condition === JobCondition.CUSTOM ? formData.customCron : undefined,
      };

      onSubmit(payload as CreateJobPayload);
    }
  };

  const handleFileSelect = (fileIds: string[], jobFiles: any[]) => {
    setSelectedFiles(jobFiles);
    setSelectedFileIds(fileIds);
  };

  const handleParameterChange = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [key]: value,
      },
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      {/* Job Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Job Name <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter job name"
          className={`w-full px-4 py-2 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
        />
        {errors.name && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" />
            {errors.name}
          </div>
        )}
      </div>

      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Template <span className="text-red-600">*</span>
        </label>
        <select
          value={formData.templateId}
          onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.templateId ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
        >
          <option value="">Select a template</option>
          {JOB_TEMPLATES.map((template) => (
            <option key={template.id} value={template.id}>
              {template.icon} {template.name}
            </option>
          ))}
        </select>
        {errors.templateId && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" />
            {errors.templateId}
          </div>
        )}
      </div>

      {/* Template Description */}
      {selectedTemplate && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
          <p className="font-medium">{selectedTemplate.name}</p>
          <p className="text-blue-800 text-xs mt-1">{selectedTemplate.description}</p>
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Enter job description (optional)"
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* File Selection */}
      {selectedTemplate && selectedTemplate.inputFileTypes.length > 0 && (
        <div>
          <FileSelector
            onFileSelect={handleFileSelect}
            onFileUpload={onFileUpload}
            acceptedFileTypes={selectedTemplate.inputFileTypes.join(',')}
            loading={loading}
            selectedFileIds={selectedFileIds}
          />
          {selectedFiles.length > 0 && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-900">
              <p className="font-medium">Selected files: {selectedFiles.length}</p>
              <ul className="mt-2 space-y-1">
                {selectedFiles.map((file) => (
                  <li key={file.id} className="text-xs">• {file.name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Condition */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Execution Condition
        </label>
        <select
          value={formData.condition}
          onChange={(e) => setFormData({ ...formData, condition: e.target.value as JobCondition })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={JobCondition.ONCE}>Once (Immediate)</option>
          <option value={JobCondition.DAILY}>Daily</option>
          <option value={JobCondition.WEEKLY}>Weekly</option>
          <option value={JobCondition.MONTHLY}>Monthly</option>
          <option value={JobCondition.CUSTOM}>Custom Cron</option>
        </select>
      </div>

      {/* Scheduled For */}
      {formData.condition !== JobCondition.ONCE && formData.condition !== JobCondition.CUSTOM && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date & Time
          </label>
          <input
            type="datetime-local"
            value={formData.scheduledFor}
            onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      )}

      {/* Custom Cron */}
      {formData.condition === JobCondition.CUSTOM && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cron Expression <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={formData.customCron}
            onChange={(e) => setFormData({ ...formData, customCron: e.target.value })}
            placeholder="e.g., 0 0 * * * (daily at midnight)"
            className={`w-full px-4 py-2 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.customCron ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
          />
          <p className="mt-2 text-xs text-gray-600">
            Format: minute hour day month weekday (use <code className="bg-gray-100 px-1 py-0.5 rounded">*</code> for any value)
          </p>
          {errors.customCron && (
            <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4" />
              {errors.customCron}
            </div>
          )}
        </div>
      )}

      {/* Template Parameters */}
      {selectedTemplate && Object.keys(selectedTemplate.parameters).length > 0 && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-5">
          <h4 className="font-medium text-gray-900 text-sm">Template Settings</h4>
          {Object.entries(selectedTemplate.parameters).map(([key, defaultValue]) => {
            const metadata = (selectedTemplate as any).parameterMetadata?.[key];
            const isArray = Array.isArray(defaultValue);
            const isObject = typeof defaultValue === 'object' && defaultValue !== null && !isArray;

            return (
              <div key={key} className="border-l-4 border-blue-400 pl-4 py-2">
                <label className="block text-sm font-semibold text-gray-900 mb-1 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>

                {/* Description */}
                {metadata?.description && (
                  <p className="text-xs text-gray-700 mb-2">{metadata.description}</p>
                )}

                {/* Example */}
                {metadata?.example && (
                  <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                    <strong>Example:</strong> {metadata.example}
                  </div>
                )}

                {/* Hint */}
                {metadata?.hint && (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                    <strong>💡 Tip:</strong> {metadata.hint}
                  </div>
                )}

                {/* Input Field */}
                {typeof defaultValue === 'boolean' ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.parameters[key] ?? defaultValue}
                      onChange={(e) => handleParameterChange(key, e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Enable this option</span>
                  </label>
                ) : typeof defaultValue === 'number' ? (
                  <input
                    type="number"
                    value={formData.parameters[key] ?? defaultValue}
                    onChange={(e) => handleParameterChange(key, Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : isArray || isObject ? (
                  <div className="p-3 bg-white border border-gray-200 rounded-lg text-xs font-mono whitespace-pre-wrap break-words max-h-64 overflow-auto">
                    {JSON.stringify(defaultValue, null, 2)}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.parameters[key] ?? defaultValue}
                    onChange={(e) => handleParameterChange(key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : job ? 'Update Job' : 'Create Job'}
        </button>
      </div>
    </form>
  );
};
